import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Download,
  X,
  Github,
  Linkedin,
  Mail,
  Code2,
  Server,
  ChevronDown,
} from "lucide-react";

import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

const Home = () => {
  const [showResume, setShowResume] = useState(false);

  const resumeView =
    "https://drive.google.com/file/d/1JST-jDldFbOHH6Pt9X1grxwP_dL9NOt9/view";

  const resumeDownload =
    "https://drive.google.com/uc?export=download&id=1JST-jDldFbOHH6Pt9X1grxwP_dL9NOt9";

  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowResume(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const scrollToProjects = () => {
    const projects = document.querySelector("#projects");

    if (projects) {
      projects.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <>
      {/* =========================================================
          HERO SECTION
      ========================================================== */}
      <section
        id="home"
        className="relative min-h-screen overflow-hidden bg-[#07070a] text-white"
      >
        {/* =====================================================
            BACKGROUND
        ====================================================== */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Multi-color ambient gradient */}
          <div
            className="
              absolute inset-0
              bg-[radial-gradient(circle_at_12%_18%,rgba(249,115,22,0.12),transparent_30%),
              radial-gradient(circle_at_88%_20%,rgba(139,92,246,0.10),transparent_30%),
              radial-gradient(circle_at_75%_78%,rgba(59,130,246,0.08),transparent_32%),
              radial-gradient(circle_at_20%_85%,rgba(236,72,153,0.06),transparent_28%)]
            "
          />

          {/* Dark base gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#08080c] via-[#0b0b12] to-[#0e0d15]" />

          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          {/* =================================================
              ORANGE ORB
          ================================================== */}
          <motion.div
            animate={{
              x: [0, 50, 0],
              y: [0, -40, 0],
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -left-40 -top-40 h-[500px] w-[500px] rounded-full bg-orange-500/10 blur-[120px]"
          />

          {/* =================================================
              PURPLE ORB
          ================================================== */}
          <motion.div
            animate={{
              x: [0, -45, 0],
              y: [0, 35, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-40 top-[10%] h-[520px] w-[520px] rounded-full bg-purple-500/[0.08] blur-[130px]"
          />

          {/* =================================================
              BLUE ORB
          ================================================== */}
          <motion.div
            animate={{
              x: [0, 35, 0],
              y: [0, -30, 0],
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 17,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-52 right-[15%] h-[450px] w-[450px] rounded-full bg-blue-500/[0.07] blur-[130px]"
          />

          {/* =================================================
              PINK / MAGENTA GLOW
          ================================================== */}
          <motion.div
            animate={{
              x: [0, -25, 0],
              y: [0, 25, 0],
              scale: [1, 1.06, 1],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute bottom-[10%] left-[15%] h-[320px] w-[320px] rounded-full bg-pink-500/[0.045] blur-[120px]"
          />

          {/* Central Orange Glow */}
          <div className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500/[0.035] blur-[130px]" />

          {/* =================================================
              PARTICLES
          ================================================== */}
          <Particles
            id="portfolio-particles"
            init={particlesInit}
            options={{
              fullScreen: {
                enable: false,
              },

              fpsLimit: 45,

              detectRetina: true,

              particles: {
                number: {
                  value: 32,

                  density: {
                    enable: true,
                    area: 1000,
                  },
                },

                color: {
                  value: ["#f97316", "#a855f7", "#3b82f6"],
                },

                opacity: {
                  value: 0.2,
                },

                size: {
                  value: {
                    min: 1,
                    max: 2,
                  },
                },

                links: {
                  enable: true,
                  distance: 170,
                  color: "#f97316",
                  opacity: 0.1,
                  width: 1,
                },

                move: {
                  enable: true,
                  speed: 0.35,
                  direction: "none",
                  random: true,
                  straight: false,

                  outModes: {
                    default: "out",
                  },
                },
              },

              interactivity: {
                events: {
                  onHover: {
                    enable: true,
                    mode: "grab",
                  },

                  resize: true,
                },

                modes: {
                  grab: {
                    distance: 140,

                    links: {
                      opacity: 0.25,
                    },
                  },
                },
              },
            }}
            className="absolute inset-0"
          />
        </div>

        {/* =====================================================
            MAIN CONTENT
        ====================================================== */}
        <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-6 pb-16 pt-28 lg:px-10">
          <div className="grid w-full items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            {/* =================================================
                LEFT SIDE
            ================================================== */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},

                visible: {
                  transition: {
                    staggerChildren: 0.12,
                  },
                },
              }}
              className="text-center lg:text-left"
            >
              {/* Badge */}
              <motion.div
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 20,
                  },

                  visible: {
                    opacity: 1,
                    y: 0,

                    transition: {
                      duration: 0.6,
                    },
                  },
                }}
              >
                <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/[0.06] px-4 py-2">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />

                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>

                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-300">
                    Backend Java Developer
                  </span>
                </div>
              </motion.div>

              {/* Heading */}
              <motion.h1
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 25,
                  },

                  visible: {
                    opacity: 1,
                    y: 0,

                    transition: {
                      duration: 0.7,
                    },
                  },
                }}
                className="text-5xl font-black leading-[1.04] tracking-[-0.04em] sm:text-6xl lg:text-[76px]"
              >
                Building
                <span className="block bg-gradient-to-r from-orange-400 via-orange-500 to-amber-400 bg-clip-text text-transparent">
                  Scalable
                </span>
                <span className="block">Backend Systems.</span>
              </motion.h1>

              {/* Description */}
              <motion.p
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 20,
                  },

                  visible: {
                    opacity: 1,
                    y: 0,

                    transition: {
                      duration: 0.7,
                    },
                  },
                }}
                className="mx-auto mt-7 max-w-2xl text-base leading-8 text-gray-400 sm:text-lg lg:mx-0"
              >
                I'm Arun, a backend-focused Java developer building reliable
                REST APIs, microservices, and production-ready applications
                using{" "}
                <span className="font-medium text-gray-200">
                  Java, Spring Boot, JPA, Hibernate, and MySQL
                </span>
                .
              </motion.p>

              {/* =================================================
                  BUTTONS
              ================================================== */}
              <motion.div
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 20,
                  },

                  visible: {
                    opacity: 1,
                    y: 0,

                    transition: {
                      duration: 0.7,
                    },
                  },
                }}
                className="mt-9 flex flex-col justify-center gap-4 sm:flex-row lg:justify-start"
              >
                {/* Explore Work */}
                <button
                  onClick={scrollToProjects}
                  className="group inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-7 py-3.5 font-semibold text-white shadow-xl shadow-orange-500/20 transition duration-300 hover:-translate-y-1 hover:shadow-orange-500/30"
                >
                  Explore My Work
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>

                {/* Resume */}
                <button
                  onClick={() => setShowResume(true)}
                  className="group inline-flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-7 py-3.5 font-semibold text-gray-200 backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-orange-500/30 hover:bg-orange-500/10 hover:text-orange-300"
                >
                  <Download size={18} />
                  View Resume
                </button>
              </motion.div>

              {/* =================================================
                  SOCIAL
              ================================================== */}
              <motion.div
                variants={{
                  hidden: {
                    opacity: 0,
                  },

                  visible: {
                    opacity: 1,

                    transition: {
                      duration: 0.7,
                    },
                  },
                }}
                className="mt-9 flex items-center justify-center gap-3 lg:justify-start"
              >
                {/* GitHub */}
                <a
                  href="https://github.com/ArunPungle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-400 transition hover:border-orange-500/30 hover:bg-orange-500/10 hover:text-orange-400"
                >
                  <Github size={19} />
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/in/arun-pungle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-400 transition hover:border-orange-500/30 hover:bg-orange-500/10 hover:text-orange-400"
                >
                  <Linkedin size={19} />
                </a>

                {/* Email */}
                <a
                  href="mailto:arunpunglegto15@gmail.com"
                  aria-label="Email"
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-400 transition hover:border-orange-500/30 hover:bg-orange-500/10 hover:text-orange-400"
                >
                  <Mail size={19} />
                </a>
              </motion.div>
            </motion.div>

            {/* =================================================
                RIGHT SIDE
            ================================================== */}
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.92,
                x: 40,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                x: 0,
              }}
              transition={{
                duration: 1,
                delay: 0.25,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative flex w-full justify-center lg:justify-end"
            >
              {/* Decorative Ring 1 */}
              <div className="pointer-events-none absolute left-1/2 top-[42%] h-[390px] w-[390px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-orange-500/[0.08] sm:h-[500px] sm:w-[500px]" />

              {/* Decorative Ring 2 */}
              <div className="pointer-events-none absolute left-1/2 top-[42%] h-[310px] w-[310px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-orange-500/[0.08] sm:h-[400px] sm:w-[400px]" />

              <div className="relative w-full max-w-[560px]">
                {/* Main Glow */}
                <div className="pointer-events-none absolute inset-10 rounded-full bg-orange-500/15 blur-[90px]" />

                {/* =================================================
                    MAIN PROFILE CARD
                ================================================== */}
                <div className="relative rounded-[32px] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.015] p-5 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-7">
                  {/* Browser Header */}
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />

                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/80" />

                      <span className="h-2.5 w-2.5 rounded-full bg-green-400/80" />
                    </div>

                    <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gray-500">
                      backend.dev
                    </span>
                  </div>

                  {/* =================================================
                      IMAGE
                  ================================================== */}
                  <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#0a0a0d]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(249,115,22,0.12),transparent_45%)]" />

                    <motion.img
                      src="/Boy1.png"
                      alt="Arun Pungle Java Backend Developer"
                      className="relative z-10 mx-auto w-[85%] object-contain drop-shadow-[0_0_55px_rgba(249,115,22,0.22)]"
                      animate={{
                        y: [0, -8, 0],
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  </div>

                  {/* =================================================
                      FOCUS + ARCHITECTURE
                  ================================================== */}
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    {/* Focus */}
                    <div className="group rounded-2xl border border-white/5 bg-white/[0.025] p-4 transition-all duration-300 hover:border-orange-500/20 hover:bg-orange-500/[0.04]">
                      <p className="text-[10px] uppercase tracking-widest text-gray-600">
                        Focus
                      </p>

                      <p className="mt-1 text-sm font-semibold text-gray-200">
                        Backend Engineering
                      </p>
                    </div>

                    {/* Architecture */}
                    <div className="group rounded-2xl border border-white/5 bg-white/[0.025] p-4 transition-all duration-300 hover:border-purple-500/20 hover:bg-purple-500/[0.04]">
                      <p className="text-[10px] uppercase tracking-widest text-gray-600">
                        Architecture
                      </p>

                      <p className="mt-1 text-sm font-semibold text-gray-200">
                        Microservices
                      </p>
                    </div>
                  </div>

                  {/* =================================================
                      TECH STACK
                  ================================================== */}
                  <motion.div
                    initial={{
                      opacity: 0,
                      y: 15,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.7,
                      delay: 0.8,
                    }}
                    className="mt-3 rounded-2xl border border-white/5 bg-white/[0.025] p-4"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-gray-600">
                          Core Stack
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          Technologies I work with
                        </p>
                      </div>

                      <Code2 size={16} className="text-orange-400" />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {["Java", "Spring Boot", "JPA", "Hibernate", "MySQL"].map(
                        (tech) => (
                          <span
                            key={tech}
                            className="rounded-lg border border-orange-500/10 bg-orange-500/[0.05] px-2.5 py-1.5 text-[10px] font-medium text-orange-300 transition-all duration-300 hover:border-orange-500/25 hover:bg-orange-500/10"
                          >
                            {tech}
                          </span>
                        ),
                      )}
                    </div>
                  </motion.div>
                </div>

                {/* =================================================
                    API CARD
                ================================================== */}
                <motion.div
                  animate={{
                    y: [0, -7, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -right-2 top-14 hidden w-44 rounded-2xl border border-white/10 bg-[#111116]/90 p-4 shadow-2xl backdrop-blur-xl lg:-right-10 lg:block"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
                      <Server size={17} />
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-gray-200">
                        REST APIs
                      </p>

                      <p className="text-[10px] text-emerald-400">
                        Production Ready
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* =====================================================
            SCROLL INDICATOR
        ====================================================== */}
        <motion.button
          onClick={scrollToProjects}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            delay: 1.5,
          }}
          className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-gray-600 transition hover:text-orange-400 md:flex"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">
            Explore
          </span>

          <motion.div
            animate={{
              y: [0, 6, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
            }}
          >
            <ChevronDown size={18} />
          </motion.div>
        </motion.button>
      </section>

      {/* =========================================================
          RESUME MODAL
      ========================================================== */}
      <AnimatePresence>
        {showResume && (
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            onClick={() => setShowResume(false)}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 px-4 backdrop-blur-md"
          >
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.92,
                y: 20,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                scale: 0.92,
                y: 20,
              }}
              onClick={(event) => event.stopPropagation()}
              className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[#111116] p-8 text-center shadow-2xl"
            >
              {/* Close */}
              <button
                onClick={() => setShowResume(false)}
                className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 text-gray-500 transition hover:border-red-500/20 hover:bg-red-500/10 hover:text-red-400"
                aria-label="Close resume"
              >
                <X size={18} />
              </button>

              {/* Icon */}
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-orange-500/20 bg-orange-500/10 text-orange-400">
                <Download size={25} />
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-white">My Resume</h3>

              {/* Description */}
              <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-gray-500">
                View my professional experience, technical skills, projects, and
                backend development experience.
              </p>

              {/* Resume Buttons */}
              <div className="mt-7 grid grid-cols-2 gap-3">
                {/* View */}
                <a
                  href={resumeView}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 font-semibold text-white transition hover:bg-orange-600"
                >
                  View Resume
                  <ArrowRight size={16} />
                </a>

                {/* Download */}
                <a
                  href={resumeDownload}
                  className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-3 font-semibold text-gray-300 transition hover:border-orange-500/20 hover:bg-orange-500/10 hover:text-orange-300"
                >
                  Download
                  <Download size={16} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Home;
